// Main JavaScript entry file for Nikon Golf site
// Includes UI animations, modal handling, navigation toggling, and scroll behaviors
import "../scss/style.scss";
import MicroModal from "micromodal";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { ScrollToPlugin } from "gsap/ScrollToPlugin";

gsap.registerPlugin(ScrollTrigger, ScrollToPlugin);

// Global Variables
const floatHeader = document.querySelector(".ng-hd--fixed");
const floatNav = document.querySelector("#ng-hd-nav");
const floatHam = document.querySelector("#ng-hd-ham");
const hams = document.querySelectorAll(".ng-ham");
const heroHeader = document.querySelector(".ng-hero-hd");
const heroNav = document.querySelector("#ng-hero-nav");
const heroHam = document.querySelector("#ng-hero-ham");
const heroTitle = document.querySelector(".ng-hero-ttl");
const scrollDown = document.querySelector(".ng-scrolldown");

// Animate and show navigation menu with staggered items
const navOpen = (nav) => {
  gsap.set(nav, { display: "block" });
  gsap.to(nav, {
    duration: 0.5,
    autoAlpha: 1,
    ease: "power2.out",
    overwrite: "auto",
  });
  const items = nav.querySelectorAll(".ng-nav-item");
  gsap.fromTo(
    items,
    {
      y: 20,
      autoAlpha: 0,
    },
    {
      duration: 0.8,
      autoAlpha: 1,
      y: 0,
      ease: "power2.out",
      overwrite: "auto",
      stagger: {
        amount: 0.3,
        from: "start",
      },
    }
  );
};

// Animate and hide navigation menu, reset classes
const navClose = (nav) => {
  const items = nav.querySelectorAll(".ng-nav-item");
  gsap.to(items, {
    duration: 0.4,
    autoAlpha: 0,
    y: 20,
    ease: "power2.out",
    overwrite: "auto",
    stagger: {
      amount: 0.2,
      from: "end",
    },
  });
  gsap.to(nav, {
    duration: 0.4,
    autoAlpha: 0,
    ease: "power2.in",
    overwrite: "auto",
    delay: 0.1,
    onComplete: () => {
      gsap.set(nav, { display: "none" });
    },
  });

  const ham = nav.closest(".ng-hd").querySelector(".ng-ham");
  ham.classList.remove("ng-ham--open");
  nav.classList.remove("ng-nav--open");
};

// Toggle ARIA attributes for accessibility
const toggleAttribute = (nav, ham, isOpen) => {
  if (isOpen === true) {
    nav.setAttribute("aria-hidden", "false");
    ham.setAttribute("aria-expanded", "true");
  } else {
    setTimeout(() => {
      nav.setAttribute("aria-hidden", "true");
      ham.setAttribute("aria-expanded", "false");
    }, 300);
  }
};

// Initialize MicroModal with GSAP-based fade animations
const modalInit = () => {
  const modalElements = document.querySelectorAll(".modal");
  modalElements.forEach((modal) => {
    gsap.set(modal, {
      autoAlpha: 0,
    });
  });

  MicroModal.init({
    disableScroll: false,
    awaitOpenAnimation: true,
    awaitCloseAnimation: true,
    onShow: (modal) => {
      document.body.style.overflow = "hidden";
      const modalElement = document.getElementById(modal.id);
      gsap.set(modalElement, {
        display: "block",
      });
      gsap.to(modalElement, {
        duration: 0.5,
        autoAlpha: 1,
        ease: "power2.out",
        onComplete: () => {
          document.body.classList.add("modal-open");
        },
      });
    },
    onClose: (modal) => {
      document.body.style.overflow = "";
      const modalElement = document.getElementById(modal.id);
      gsap.to(modalElement, {
        duration: 0.5,
        autoAlpha: 0,
        ease: "power2.out",
        onComplete: () => {
          document.body.classList.remove("modal-open");
          gsap.set(modalElement, {
            display: "none",
          });
        },
      });
    },
  });
};

// Initial fade-in animations for key elements
const loadAnimation = () => {
  const elem1 = document.querySelectorAll(".ng-elem-1");
  const elem2 = document.querySelectorAll(".ng-elem-2");

  if (elem1.length > 0 || elem2.length > 0) {
    elem1.forEach((el) => {
      gsap.to(el, {
        delay: 0.2,
        duration: 3,
        autoAlpha: 1,
        ease: "power2.out",
      });
    });

    elem2.forEach((el) => {
      gsap.to(el, {
        duration: 3,
        autoAlpha: 1,
        ease: "power2.out",
        delay: 1,
      });
    });
  }
};

// Show scroll-to-top button when scrolled past certain point
const toggleScrollTopBtn = () => {
  const scrollTopBtn = document.querySelector(".ng-scrolltop");
  if (scrollTopBtn) {
    ScrollTrigger.create({
      trigger: "#top",
      start: "bottom bottom",
      end: "bottom center",
      onLeave: () => {
        scrollTopBtn.classList.add("ng-scrolltop--visible");
      },
      onEnterBack: () => {
        scrollTopBtn.classList.remove("ng-scrolltop--visible");
      },
    });
  }
};

// Show outro section and hide header when user scrolls to product area
const outroAnimation = () => {
  const outroElement = document.querySelector(".ng-ft-outro");
  const triggerElement = document.querySelector(".ng-prod-cont");
  if (outroElement && triggerElement) {
    gsap.to(outroElement, {
      scrollTrigger: {
        trigger: triggerElement,
        start: "bottom center",
        end: "bottom top",
        invalidateOnRefresh: true,
        onEnter: () => {
          gsap.to(outroElement, {
            duration: 1.5,
            autoAlpha: 1,
            ease: "power2.out",
            overwrite: "auto",
          });
          floatHeader.classList.add("ng-hd--out");

          if (floatNav.classList.contains("ng-nav--open")) {
            toggleAttribute(floatNav, floatHam, false);
            navClose(floatNav);
          }
        },
        onLeaveBack: () => {
          gsap.to(outroElement, {
            duration: 0.5,
            autoAlpha: 0,
            ease: "power2.out",
            overwrite: "auto",
          });

          floatHeader.classList.remove("ng-hd--out");
        },
      },
    });
  }
};

// Smooth scroll behavior for anchor links with header offset
const smoothScroll = () => {
  const links = document.querySelectorAll('a[href^="#"]');
  links.forEach((link) => {
    link.addEventListener("click", (e) => {
      e.preventDefault();
      const offset = document.querySelector(".ng-hd--fixed").offsetHeight || 0;
      const targetId = link.getAttribute("href").substring(1);
      const targetElement = document.getElementById(targetId);
      if (targetElement) {
        gsap.to(window, {
          duration: 1,
          scrollTo: { y: targetElement, offsetY: offset },
          ease: "power2.out",
        });
      }
    });
  });
};

// Adjust ARIA attributes based on screen size (mobile vs desktop)
const handleAriaOnResize = () => {
  const hams = document.querySelectorAll(".ng-ham");
  const navs = document.querySelectorAll(".ng-nav");

  const isDesktop = window.matchMedia("(min-width: 1025px)").matches;

  hams.forEach((ham) => {
    if (isDesktop) {
      ham.removeAttribute("aria-expanded");
    } else {
      if (!ham.hasAttribute("aria-expanded")) {
        ham.setAttribute("aria-expanded", "false");
      }
    }
  });

  navs.forEach((nav) => {
    if (isDesktop) {
      nav.removeAttribute("aria-hidden");
    } else {
      if (!nav.hasAttribute("aria-hidden")) {
        nav.setAttribute("aria-hidden", "true");
      }
    }
  });
};

// Handle nav toggle buttons and floating header visibility on scroll
const headerFunc = () => {
  hams.forEach((ham) => {
    const navId = ham.getAttribute("aria-controls");
    const nav = document.getElementById(navId);
    let isOpen = false;

    ham.addEventListener("click", () => {
      ham.classList.toggle("ng-ham--open");
      nav.classList.toggle("ng-nav--open");

      isOpen = nav.classList.contains("ng-nav--open");

      toggleAttribute(nav, ham, isOpen);

      if (isOpen) {
        navOpen(nav);
      } else {
        navClose(nav);
      }
    });
  });

  const navLinks = document.querySelectorAll(".ng-nav-link");
  navLinks.forEach((link) => {
    link.addEventListener("click", (e) => {
      const ham = link.closest(".ng-hd").querySelector(".ng-ham");
      ham.focus();

      const navId = ham.getAttribute("aria-controls");
      const nav = document.getElementById(navId);
      toggleAttribute(nav, ham, false);
      navClose(nav);
    });
  });

  // Floating Header Animation
  if (floatHeader) {
    window.addEventListener("scroll", () => {
      const scrollPosition = window.scrollY;
      if (scrollPosition > 20) {
        floatHeader.classList.remove("ng-hd--hidden");
        heroTitle.classList.add("ng-hero-ttl--hidden");
        heroHeader.classList.add("ng-hd--hidden");
        scrollDown.querySelector("img").classList.add("ng-scrolldown--hidden");

        // Open floating nav if closed
        if (heroNav.classList.contains("ng-nav--open")) {
          toggleAttribute(heroNav, heroHam, false);
          navClose(heroNav);
        }
      } else {
        floatHeader.classList.add("ng-hd--hidden");
        heroTitle.classList.remove("ng-hero-ttl--hidden");
        heroHeader.classList.remove("ng-hd--hidden");
        scrollDown.querySelector("img").classList.remove("ng-scrolldown--hidden");

        // Close floating nav if open
        if (floatNav.classList.contains("ng-nav--open")) {
          toggleAttribute(floatNav, floatHam, false);
          navClose(floatNav);
        }
      }
    });
  }
};

// Background carousel with image slide animation and autoplay using GSAP
const bgCarousel = () => {
  const bg = document.querySelector(".ng-bg");
  if (bg) {
    const slides = bg.querySelectorAll(".ng-bg-item");
    const slideLen = slides.length;
    let current = 0;
    let timer;
    let slideState = "";
    const duration = 5000;
    const move = 2000;
    const imgAnimDuration = (duration + move) / 1000;
    let imgAnimStartPos = 30;
    let imgAnimEndPos = -30;
    if (window.matchMedia("(min-width: 768px)").matches) {
      imgAnimStartPos = 40;
      imgAnimEndPos = -40;
    }

    const imgAnim = (images) => {
      images.forEach((image) => {
        gsap.killTweensOf(image);
        gsap.set(image, { x: imgAnimStartPos, duration: 0 });
        gsap.to(image, {
          duration: imgAnimDuration,
          x: imgAnimEndPos,
          ease: "none",
        });
      });
    };

    const init = () => {
      bg.setAttribute("data-isPlaying", "false");
      slides[current].classList.add("is-current");

      gsap.set(slides, {
        autoAlpha: 0,
        duration: 0,
      });
      gsap.set(slides[current], {
        autoAlpha: 1,
        duration: 0,
      });
      const images = slides[current].querySelectorAll("img");
      imgAnim(images);
    };
    init();

    const slideChange = (index) => {
      if (index === current || slideState === "moving") return;
      slideState = "moving";
      const oldCurrent = current;
      current = index;

      gsap.set(slides[current], {
        zIndex: 1,
        duration: 0,
      });

      const images = slides[current].querySelectorAll("img");
      imgAnim(images);

      const tl = gsap.timeline();
      tl.to(slides[current], {
        autoAlpha: 1,
        duration: move / 1000,
        ease: "none",
        onStart: () => {
          slides[current].classList.add("is-current");
          slides[oldCurrent].classList.remove("is-current");
          slides[oldCurrent].classList.add("is-after");
        },
        onComplete: () => {
          slides[oldCurrent].classList.remove("is-after");
          gsap.set(slides[oldCurrent], {
            autoAlpha: 0,
            duration: 0,
          });
          gsap.set(slides[current], {
            zIndex: 0,
          });
          slideState = "";
        },
      });

      clearTimeout(timer);
      slideTimer();
    };

    const slidePlay = () => {
      if (bg.getAttribute("data-isPlaying") === "true") return;
      bg.setAttribute("data-isPlaying", "true");
      slideTimer();
    };

    const slideTimer = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        let index = current + 1;
        if (index >= slideLen) {
          index = 0;
        }
        slideChange(index);
      }, duration);
    };

    ScrollTrigger.create({
      trigger: bg,
      start: "top center",
      onEnter: () => {
        slidePlay();
      },
    });
  }
};

// Fade-in animation for elements on scroll
const scrollAnimation = () => {
  const fadeInGroups = document.querySelectorAll(".ng-fadein-group");
  fadeInGroups.forEach((group) => {
    const items = group.querySelectorAll(".ng-fadein-item");

    gsap.set(items, {
      autoAlpha: 0,
      y: 40,
    });

    ScrollTrigger.create({
      trigger: group,
      start: "top 65%",
      onEnter: () => {
        gsap.to(items, {
          duration: 0.85,
          autoAlpha: 1,
          y: 0,
          ease: "power2.out",
          overwrite: "auto",
          stagger: {
            amount: 0.2,
            from: "start",
          },
        });
      },
    });
  });

  const fadeIns = document.querySelectorAll(".ng-fadein");
  fadeIns.forEach((fadeIn) => {
    gsap.set(fadeIn, {
      autoAlpha: 0,
      y: 40,
    });

    ScrollTrigger.create({
      trigger: fadeIn,
      start: "top 75%",
      onEnter: () => {
        gsap.to(fadeIn, {
          duration: 0.85,
          autoAlpha: 1,
          y: 0,
          ease: "power2.out",
          overwrite: "auto",
        });
      },
    });
  });
};

// Initialize all components after DOM is ready
document.addEventListener("DOMContentLoaded", () => {
  window.scrollTo(0, 0);
  modalInit();
  loadAnimation();
  toggleScrollTopBtn();
  outroAnimation();
  smoothScroll();
  headerFunc();
  bgCarousel();
  handleAriaOnResize();
  window.addEventListener("resize", handleAriaOnResize);
  scrollAnimation();
});
